import { t } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { DEFAULT_API_CONFIG } from "../../api/api-config";
import useDataFetch from "../../hooks/useDataFetch";
import { exportToCSV } from "../../utils/csv-export";
import { ISubscription } from "../../utils/data-types";
import { removeTrailingZeros } from "../../utils/functions";
import { ExportButton } from "../buttons/ExportButton";
import { Icon } from "../icon/Icon";
import { Text } from "../text/Text";
import "./SubscriptionTable.scss";

interface SubscriptionTableProps {
  customerId: number | undefined;
  customerName: string | undefined;
}

export const SubscriptionTable = ({
  customerId,
  customerName,
}: SubscriptionTableProps) => {
  const [displayedRows, setDisplayedRows] = useState(5);
  const amountOfExtraRowsToShow = 5;

  const [url, setUrl] = useState<string>(
    DEFAULT_API_CONFIG.url + "/crm/abonnement/get/" + customerId
  );

  useEffect(() => {
    if (customerId && customerId !== 0) {
      setUrl(DEFAULT_API_CONFIG.url + "/crm/abonnement/get/" + customerId);
    }
  }, [customerId]);

  const [data] = useDataFetch<ISubscription[]>(url, "GET");

  const exportData = useMemo(() => {
    const tariffnameTranslation = t("pages.customersInfo.tariffname");
    const quantityTranslation = t("pages.customersInfo.quantity");
    return data?.map((subscription) => {
      return {
        ID: subscription.id,
        Tariff: subscription.tariff,
        [tariffnameTranslation]: subscription.tariffnavn,
        Hentepunkt: subscription.hentepunkt,
        [quantityTranslation]: removeTrailingZeros(subscription.antall),
        Pris: removeTrailingZeros(subscription.pris),
      };
    });
  }, [data]);

  const handleExportData = useCallback(() => {
    if (exportData) {
      exportToCSV(exportData, "ngir_" + customerName + "_subscription");
    }
  }, [customerName, exportData]);

  const showMoreString = t("pages.customersInfo.showMore", {
    amount:
      data?.length === undefined
        ? 0
        : amountOfExtraRowsToShow + displayedRows > data?.length
        ? data?.length
        : amountOfExtraRowsToShow + displayedRows,
  });

  const showAllString = t("pages.customersInfo.showAll", {
    amount: data?.length,
  });

  return (
    <div className="subscription-table" id="table">
      <div className="subscription-table__header">
        <Text
          text={t("pages.customersInfo.subscription")}
          size="small"
          color="dark"
          fontWeight={600}
        />
        <ExportButton onClick={handleExportData} isCsv />
      </div>

      <div className="subscription-table__content">
        <div className="table-header">
          <div className="table-cell">
            <Text text={t("pages.customersInfo.id")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customersInfo.tariff")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customersInfo.tariffname")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customersInfo.pickUpPoint")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customersInfo.quantity")} size="xxxs" />
          </div>
          <div className="table-cell">
            <Text text={t("pages.customersInfo.price")} size="xxxs" />
          </div>
        </div>
        <div className="subscription-table__rows">
          {data &&
            data.slice(0, displayedRows).map((row, rowIndex: number) => (
              <div className="table-row" key={rowIndex}>
                <div className="table-cell">
                  <Text
                    text={data[rowIndex].id.toString()}
                    size="xxs"
                    fontWeight={600}
                  />
                </div>
                <div className="table-cell">
                  <Text text={data[rowIndex].tariff} size="xxs" />
                </div>
                <div className="table-cell">
                  <Text text={data[rowIndex].tariffnavn} size="xxs" />
                </div>
                <div className="table-cell">
                  <Text text={data[rowIndex].hentepunkt} size="xxs" />
                </div>
                <div className="table-cell">
                  <Text
                    text={removeTrailingZeros(data[rowIndex].antall)}
                    size="xxs"
                  />
                </div>
                <div className="table-cell">
                  <Text
                    text={removeTrailingZeros(data[rowIndex].pris)}
                    size="xxs"
                  />
                </div>
              </div>
            ))}
          {data && displayedRows < data.length && (
            <div className="table-row show-more-container">
              <div
                className="show-more"
                onClick={() =>
                  setDisplayedRows(displayedRows + amountOfExtraRowsToShow)
                }
              >
                <Text text={showMoreString} size="xxs" fontWeight={600} />
                <Icon name="add" />
              </div>
              <div
                className="show-all"
                onClick={() => setDisplayedRows(data.length)}
              >
                <Text text={showAllString} size="xxs" fontWeight={600} />
                <Icon name="add" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
